/**
 * Node Modules
 */
import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { graphql } from 'gatsby'

/**
 * Application Modules
 */
import PageContainer from '../../components/page-container'
import Metadata from '../../components/metadata'
import Footer from '../../components/footer'

import Hero from '../../sections/life-at-kinzoo/hero'
import OurTeam from '../../sections/life-at-kinzoo/our-team'
import OurAdvisors from '../../sections/life-at-kinzoo/our-advisors'
import OurOffice from '../../sections/life-at-kinzoo/our-office'

import OpenRoles from '../../sections/life-at-kinzoo/open-roles'
import DownloadBanner from '../../components/download-banner'
import UserQuotes from '../../sections/home/user-quote'
import Section from '../../components/section'
import Spacer from '../../components/spacer'

import './styles.css'

function SocialProof({quotes}) {
  const styles = {
    '--color': 'var(--light-purple)',
    '--background-color': 'var(--light-purple)',
  }

  const leftColumn = "column-start-1 column-end-6 column-start-1--t column-start-2--d column-end-6--t"
  const rightColumn = "column-start-1 column-end-6 column-start-7--t column-end-12--t column-end-11--d"

  const totalQuotes = quotes.length;
  const [currentQuote, setCurrentQuote] = useState(0)

  useEffect(() => {
    if (typeof window === 'undefined') return false;
    const cards = document.querySelectorAll('.home-social-proof__quote-card');
    let largest = 0;

    cards.forEach(card => {
      card.style.height = `auto`
    })

    cards.forEach(card => {
      if (card.offsetHeight > largest) largest = card.offsetHeight;
    })

    console.log(largest)
    cards.forEach(card => {
      card.style.height = `${largest}px`
    })
  }, [])

  useEffect(()=> {
    const timeout = setTimeout(()=> {
      if (typeof window === 'undefined') return false;
      const incrementAmount = window.innerWidth > 767 ? 2 : 1
      setCurrentQuote((currentQuote + incrementAmount) % totalQuotes);
    }, 7000)
    return () => { clearTimeout(timeout) }
  }, [currentQuote])

  return (
    <Section className="home-social-proof life-at-kinzoo__social-proof" styles={styles} >
      <div className={"home-social-proof-grid grid"}>
        {
          quotes.map((quote, i) => {
            const isActive = currentQuote === i ? 'active' : ''
            const isNext = (currentQuote + 1) % totalQuotes === i ? 'active-next' : ''
            const column = i % 2 ? rightColumn : leftColumn

            return (
              <div className={`home-social-proof__quote ${isActive} ${isNext} ${column}`} key={'quote_' + i} >
                {!!(i % 2) && <Spacer size={[0, 100]} />}
                <div className={`home-social-proof__quote-card flex flex-center`}>
                  <div>
                    <Spacer size={[55, 40]} />
                    <div className={'home-social-proof__photo-wrapper'}>
                      <img className={'home-social-proof__photo'} src={quote.referenceQuoteImage.defaultImage.file.url} alt={quote.referenceQuoteImage.description} />
                    </div>
                    <Spacer size={[15, 24]} />
                    <p className={'home-social-proof__quote-copy'}>{quote.quoteCopy.quoteCopy}</p>
                    <Spacer size={[15, 15]} />
                    <p className={'home-social-proof__quote-byline'}>
                      <span className={'home-social-proof__quote-author'}>{quote.quoteByline}</span><br/>
                      <span className={'home-social-proof__quote-author-title'}>{quote.quoteDescriptor}</span>
                    </p>
                    <Spacer size={[55, 40]} />
                  </div>
                </div>
              </div>
            )
          })
        }
      </div>
      <Spacer size={[20, 100]} />
    </Section>
  )
}

/**
 * Page Component
 */
export default function Page({ data }) {
  const {
    referencePageMetadata,
    heroDek,
    heroHed,
    leadershipTeam,
    creativeTeam,
    techTeam,
    customerTeam,
    referenceQuoteCarousel,
    ourAdvisorsHed,
    ourAdvisorsDek,
    advisorDetails,
    ourOfficeHed,
    ourOfficeDek,
    openRolesDek,
    openRolesHed,
    openRolesData,
  } = data.contentfulSingletonLifeAtKinzoo

  return (
    <PageContainer>
      <Helmet>
        <link rel="canonical" href="https://kinzoo.com/life-at-kinzoo/" />
        <meta property="og:url" content="https://kinzoo.com/life-at-kinzoo/" />
      </Helmet>
      <Metadata {...referencePageMetadata} />
      <Hero hed={heroHed} dek={heroDek.heroDek} />
      <OurTeam leadershipTeam={leadershipTeam} creativeTeam={creativeTeam} techTeam={techTeam} customerTeam={customerTeam} />
      <SocialProof quotes={referenceQuoteCarousel} />
      <OurAdvisors
        hed={ourAdvisorsHed}
        dek={ourAdvisorsDek.ourAdvisorsDek}
        advisorDetails={advisorDetails}
      />
      <OurOffice hed={ourOfficeHed} dek={ourOfficeDek.ourOfficeDek} />
      <OpenRoles hed={openRolesHed} dek={openRolesDek.openRolesDek} openRolesData={openRolesData} />
      <DownloadBanner />
      <Footer />
    </PageContainer>
  )
}

export const pageQuery = graphql`
  query LifeAtKinzooPageQuery {
    contentfulSingletonLifeAtKinzoo(contentful_id: { eq: "5nuSJfjARdPTEyAK5AvDbg" }) {
      referencePageMetadata {
        pageTitle
        ogTitle
        twitterTitle
        description
        ogDescription
        twitterDescription
        ogImage {
          file {
            url
          }
        }
        twitterImage {
          file {
            url
          }
        }
      }

      leadershipTeam {
        name
        title
        photo {
          file {
            url
          }
        }
      }

      creativeTeam {
        name
        title
        photo {
          file {
            url
          }
        }
      }

      techTeam {
        name
        title
        photo {
          file {
            url
          }
        }
      }

      customerTeam {
        name
        title
        photo {
          file {
            url
          }
        }
      }

      referenceQuoteCarousel {
        quoteByline
        quoteDescriptor
        quoteCopy {
          quoteCopy
        }
        referenceQuoteImage {
          description
          defaultImage {
            file {
              url
            }
          }
        }
      }

      advisorDetails {
        brand
        dek
        image
        name
      }
      heroDek {
        heroDek
      }
      heroHed
      openRolesData {
        href
        text
      }
      openRolesDek {
        openRolesDek
      }
      openRolesHed
      ourAdvisorsDek {
        ourAdvisorsDek
      }
      ourAdvisorsHed
      ourOfficeDek {
        ourOfficeDek
      }
      ourOfficeHed
      ourTeamDek {
        ourTeamDek
      }
      ourTeamHed
    }
  }
`
